/**
 * Uygulama renk paleti — mobil (e-doctor-mobile) ile aynı tek kaynak yapı.
 *
 * Kullanım örnekleri:
 *   import { palette, appColors } from "../constants/colors";
 *   color={palette.brand.main}
 *   backgroundColor={palette.button.default.background}
 *
 *   import { colors } from "../constants/colors";
 *   color={colors.primary}
 *
 * Web UI geriye dönük uyumluluk için düz renkler:
 *   import { appColors } from "../constants/theme";
 */

/**
 * Marka paleti — ana rengi test/değiştirmek için SADECE bu bloğu güncelle.
 * button.default, message, status.appointment otomatik buradan türetilir.
 */
const brandColors = {
  main: "#0891B2",
  dark: "#0E7490",
  dark2: "#155E75",
  light: "#A5F3FC",
  50: "#ECFEFF",
  100: "#CFFAFE",
  200: "#A5F3FC",
  pageTint: "#F0F9FF",
};

/** Ham renk değerleri — değişiklikler öncelikle burada yapılır */
export const palette = {
  brand: brandColors,
  success: {
    main: "#059669",
    dark: "#047857",
    light: "#A7F3D0",
  },
  error: {
    main: "#C10000",
    light: "#FFCDDD",
    red: "#EF4444",
    contrast: "#D81159",
  },
  warning: {
    main: "#FF6D3A",
    secondary: "#FFCC00",
    orange: "#F59E0B",
    light: "#FFF3E0",
  },
  text: {
    main: "#0F172A",
    secondary: "#64748B",
    tertiary: "#9CA3AF",
    placeholder: "#ADAEBC",
    gray: "#4B5563",
    dark: "#1F2937",
    light: "#617583",
    muted: "#979797",
    black: "#000000",
    white: "#FFFFFF",
  },
  background: {
    main: "#FFFFFF",
    secondary: "#E5E7EB",
    light: brandColors.pageTint,
    app: "#F4F5F6",
    muted: "#EDEDED",
  },
  border: {
    main: "#E2E8F0",
    light: "#D1D5DB",
    gray: "#F3F4F6",
  },
  gray: {
    light: "#E2E2E2",
    text: "#6B7280",
    dark: "#1F2937",
    darkGray: "#374151",
    neutral: "#617583",
  },
  status: {
    green: "#059669",
    orange: "#F59E0B",
    blue: brandColors.main,
    purple: "#8B5CF6",
    appointment: brandColors.main,
  },
  accent: {
    purple: "#8B5CF6",
    blue: "#2563EB",
    red: "#DC2626",
  },
  button: {
    default: {
      main: brandColors.main,
      dark: brandColors.dark,
      light: brandColors.light,
      background: brandColors[50],
    },
    success: {
      main: "#059669",
      dark: "#047857",
      light: "#A7F3D0",
      background: "#D1FAE5",
    },
    error: {
      main: "#E63B3B",
      dark: "#C10000",
      light: "#FFCDDD",
      background: "#F7EAEA",
    },
    neutral: {
      main: "#666666",
      dark: "#4B5563",
      light: "#9CA3AF",
      background: "#EEEEEE",
    },
    secondary: {
      main: "#2cbddc",
      dark: "#0E7490",
      light: "#A5F3FC",
      background: "#dcebf3",
    },
    disabled: {
      main: "#9CA3AF",
      dark: "#6B7280",
      light: "#F3F4F6",
      background: "#E5E7EB",
      text: "#9CA3AF",
    },
  },
  message: {
    main: brandColors.main,
    mainLight: brandColors[50],
  },
  common: {
    white: "#FFFFFF",
    black: "#121212",
  },
  overlay: {
    scrim: "rgba(0,0,0,0.5)",
  },
};

/** Kısayol export — tek renk ihtiyacı olan bileşenler için */
export const colors = {
  primary: palette.brand.main,
  primaryDark: palette.brand.dark,
  primaryDark2: palette.brand.dark2,
  primaryLight: palette.brand.light,
  primaryBg: palette.brand[50],
  success: palette.success.main,
  successDark: palette.success.dark,
  successLight: palette.success.light,
  error: palette.error.main,
  errorRed: palette.error.red,
  warning: palette.warning.main,
  text: palette.text.main,
  textSecondary: palette.text.secondary,
  background: palette.background.main,
  backgroundLight: palette.background.light,
  border: palette.border.main,
  white: palette.common.white,
  black: palette.common.black,
};

/** Nested token yapısı — mobil appColors ile aynı */
export const appColorTokens = {
  primary: {
    main: palette.brand.main,
    secondary: palette.brand[50],
    secondary2: palette.brand[100],
    secondary3: palette.brand[200],
    dark: palette.brand.dark,
    dark2: palette.brand.dark2,
    light: palette.brand.light,
  },
  success: {
    main: palette.success.main,
    dark: palette.success.dark,
    light: palette.success.light,
  },
  text: {
    main: palette.text.main,
    primary: palette.text.main,
    secondary: palette.text.secondary,
    tertiary: palette.text.tertiary,
    placeholder: palette.text.placeholder,
    lightgray: palette.text.secondary,
    black: palette.text.black,
    white: palette.text.white,
    gray: palette.text.gray,
    dark: palette.text.dark,
  },
  background: {
    main: palette.background.main,
    secondary: palette.background.secondary,
    light: palette.background.light,
  },
  error: {
    main: palette.error.main,
    light: palette.error.light,
    red: palette.error.red,
  },
  border: {
    main: palette.border.main,
    light: palette.border.light,
    gray: palette.border.gray,
  },
  button: palette.button,
  status: {
    green: palette.status.green,
    orange: palette.status.orange,
    blue: palette.status.blue,
    purple: palette.status.purple,
    appointmentBlue: palette.status.appointment,
  },
  gray: palette.gray,
  accent: {
    lightTeal: palette.brand[50],
    lightBlue: palette.brand[100],
  },
  message: {
    main: palette.message.main,
    secondary: palette.message.main,
    mainLight: palette.message.mainLight,
  },
};

/** Marka rengi RGB — rgba(...) için */
export const brandRgb = "8, 145, 178";

/** Marka rengi opaklık kısayolu */
export const primaryAlpha = (opacity) => `rgba(${brandRgb}, ${opacity})`;

/**
 * Web UI düz appColors — mevcut bileşenler palette'ten türetilir.
 * import { appColors } from "./theme" ile kullanılmaya devam eder.
 */
export const flatAppColors = {
  primary: palette.brand.main,
  primaryDark: palette.brand.dark,
  primaryLight: palette.brand.light,
  primaryTeal: palette.button.secondary.main,
  primaryBg: palette.brand[50],
  primaryBgHover: palette.brand[100],
  primaryBgPale: palette.brand[200],
  primaryDeep: palette.brand.dark2,
  primaryDeepest: palette.brand.dark2,
  primaryCyan: palette.brand.dark,

  white: palette.common.white,
  black: palette.text.black,

  gray50: palette.border.gray,
  gray200: palette.border.main,
  gray500: palette.gray.text,
  gray900: palette.text.main,

  bgPage: palette.background.app,
  bgMuted: palette.background.muted,

  success: palette.success.main,
  successDark: palette.success.dark,
  successBg: palette.button.success.background,

  error: palette.error.red,
  errorDark: palette.error.main,
  errorBg: palette.error.light,

  warning: palette.warning.orange,
  warningDark: palette.warning.main,
  warningBg: palette.warning.light,

  info: palette.accent.blue,
  infoDark: palette.accent.blue,
  infoBg: palette.brand.pageTint,

  purple: palette.accent.purple,
  purpleDark: palette.accent.purple,
  purpleBg: palette.brand[50],

  landingDark: palette.gray.dark,
  landingDarker: palette.text.main,
  landingNearBlack: palette.common.black,
  landingCharcoal: palette.common.black,
  landingText: palette.text.dark,
  landingSlate: palette.text.secondary,
  landingSlateAlt: palette.gray.text,

  tealBg: palette.brand.pageTint,
  tealBorder: palette.brand[200],
  tealMint: palette.brand.light,
  tealPale: palette.brand[50],

  materialGray: palette.gray.light,
  materialGrayDark: palette.gray.darkGray,
  materialGrayText: palette.text.dark,
};

export default palette;
